import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PasswordInput from "../../components/PasswordInput/PasswordInput";
import TopBarLogin from "../../components/TopBarLogin/TopBarLogin";
import ToastModal from "../../components/ToastModal/ToastModal.jsx";
import TermsAndConditionsModal from "../../components/TermsAndConditionsModal/TermsAndConditionsModal";

import {
  Container,
  Header,
  Title,
  Ornament,
  OrnLine,
  OrnDiamond,
  Form,
  Label,
  Input,
  Select,
  CheckboxContainer,
  CheckboxLabel,
  Checkbox,
  CheckboxError,
  SubmitButton,
  Note,
} from "./RegisterPageStyles";

export default function RegisterPage() {
  const [form, setForm] = useState({
    nume: "",
    prenume: "",
    email: "",
    parola: "",
    confirmaParola: "",
    telefon: "",
    zi: "",
    luna: "",
    an: "",
    acord1: false,
    acord2: false,
    acord3: false,
  });

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateField = (name, value, formValues = form) => {
    switch (name) {
      case "nume":
        return /^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/.test(value)
          ? ""
          : "Introdu un nume valid (doar litere).";
      case "prenume":
        return /^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/.test(value)
          ? ""
          : "Introdu un prenume valid (doar litere).";
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Te rugăm să introduci un e-mail valid.";
      case "telefon":
        return /^\d{10}$/.test(value)
          ? ""
          : "Te rugăm să introduci un număr de telefon valid (10 cifre).";
      case "parola":
      case "confirmaParola":
        return formValues.parola === formValues.confirmaParola
          ? ""
          : "Parolele nu coincid.";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === "checkbox" ? checked : value;

    const actualForm = {
      ...form,
      [name]: actualValue,
    };

    setForm(actualForm);

    const error = validateField(name, actualValue, actualForm);

    let extraErrors = {};
    if (name === "parola" || name === "confirmaParola") {
      extraErrors.parola = validateField(
        "parola",
        actualForm.parola,
        actualForm,
      );
      extraErrors.confirmaParola = validateField(
        "confirmaParola",
        actualForm.confirmaParola,
        actualForm,
      );
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
      ...extraErrors,
    }));
  };

  const luniMap = {
    Ianuarie: "01",
    Februarie: "02",
    Martie: "03",
    Aprilie: "04",
    Mai: "05",
    Iunie: "06",
    Iulie: "07",
    August: "08",
    Septembrie: "09",
    Octombrie: "10",
    Noiembrie: "11",
    Decembrie: "12",
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.nume.match(/^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/)) {
      newErrors.nume = "Introdu un nume valid (doar litere).";
    }
    if (!form.prenume.match(/^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/)) {
      newErrors.prenume = "Introdu un prenume valid (doar litere).";
    }
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = "Te rugăm să introduci un e-mail valid.";
    }
    if (!/^\d{10}$/.test(form.telefon)) {
      newErrors.telefon =
        "Te rugăm să introduci un număr de telefon valid (10 cifre).";
    }
    if (!form.zi || !form.luna || !form.an || !luniMap[form.luna]) {
      newErrors.dataNasterii = "Completează data nașterii.";
    }
    if (form.confirmaParola && form.parola !== form.confirmaParola) {
      newErrors.parola = "Parolele nu coincid.";
    }
    if (!form.acord1) {
      newErrors.acord1 = "Trebuie să fii de acord cu termenii și condițiile.";
    }
    if (!form.acord2) {
      newErrors.acord2 =
        "Trebuie să fii de acord cu politica de confidențialitate.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const dataNasterii = `${form.an}-${luniMap[form.luna]}-${String(
    form.zi,
  ).padStart(2, "0")}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch(process.env.REACT_APP_GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation Register(
              $nume: String!,
              $prenume: String!,
              $email: String!,
              $parola: String!,
              $telefon: String!,
              $data_nasterii: String!,
              $prelucrare_date: Boolean!,
              $primire_oferte: Boolean!,
            ) {
              register(
                nume: $nume,
                prenume: $prenume,
                email: $email,
                parola: $parola,
                telefon: $telefon,
                data_nasterii: $data_nasterii,
                prelucrare_date: $prelucrare_date,
                primire_oferte: $primire_oferte,
              ) {
                message
              }
            }
          `,
          variables: {
            nume: form.nume,
            prenume: form.prenume,
            email: form.email,
            parola: form.parola,
            telefon: form.telefon,
            data_nasterii: dataNasterii,
            prelucrare_date: form.acord2,
            primire_oferte: form.acord3,
          },
        }),
      });

      const result = await response.json();
      if (result.errors) {
        console.error("Eroare de la server:", result.errors);
        setToastMsg("A apărut o eroare la înregistrare.");
      } else {
        setToastMsg(result.data.register.message);
        setShouldNavigate(true);
      }
    } catch (error) {
      console.error("Eroare la fetch:", error);
      setToastMsg("A apărut o eroare internă.");
    }
  };

  return (
    <>
      <TopBarLogin />

      <Container>
        <Form onSubmit={handleSubmit}>
          <Header>
            <Title>Înregistrare</Title>
            <Ornament aria-hidden="true">
              <OrnLine />
              <OrnDiamond />
            </Ornament>
          </Header>
          <div>
            <Label>Nume *</Label>
            <Input
              name="nume"
              value={form.nume}
              onChange={handleChange}
              style={errors.nume ? { border: "1px solid red" } : {}}
            />
            {errors.nume && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.nume}
              </span>
            )}
          </div>

          <div>
            <Label>Prenume *</Label>
            <Input
              name="prenume"
              value={form.prenume}
              onChange={handleChange}
              style={errors.prenume ? { border: "1px solid red" } : {}}
            />
            {errors.prenume && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.prenume}
              </span>
            )}
          </div>

          <div>
            <Label>E-mail *</Label>
            <Input
              name="email"
              type="text"
              value={form.email}
              onChange={handleChange}
              style={errors.email ? { border: "1px solid red" } : {}}
            />
            {errors.email && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.email}
              </span>
            )}
          </div>

          <div>
            <Label>Parolă *</Label>
            <PasswordInput
              name="parola"
              value={form.parola}
              onChange={handleChange}
            />
          </div>

          <div>
            <Label>Confirmă parola *</Label>
            <PasswordInput
              name="confirmaParola"
              value={form.confirmaParola}
              onChange={handleChange}
            />
            {errors.parola && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.parola}
              </span>
            )}
          </div>

          <div>
            <Label>Număr de telefon *</Label>
            <Input
              name="telefon"
              value={form.telefon}
              onChange={handleChange}
              style={errors.telefon ? { border: "1px solid red" } : {}}
            />
            {errors.telefon && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.telefon}
              </span>
            )}
          </div>

          <div>
            <Label>Data nașterii *</Label>
            <div style={{ display: "flex", gap: "1rem" }}>
              <Select name="zi" value={form.zi} onChange={handleChange}>
                <option value="">Zi</option>
                {[...Array(31)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Select>

              <Select name="luna" value={form.luna} onChange={handleChange}>
                <option value="">Lună</option>
                {Object.keys(luniMap).map((luna, i) => (
                  <option key={i} value={luna}>
                    {luna}
                  </option>
                ))}
              </Select>

              <Select name="an" value={form.an} onChange={handleChange}>
                <option value="">An</option>
                {[...Array(100)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Select>
            </div>

            {errors.dataNasterii && (
              <span style={{ color: "red", fontSize: "0.8rem" }}>
                {errors.dataNasterii}
              </span>
            )}
          </div>

          <CheckboxContainer>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="acord1"
                checked={form.acord1}
                onChange={handleChange}
              />
              <span>
                Am citit și sunt de acord cu{" "}
                <button
                  type="button"
                  onClick={() => setShowTermsModal("terms")}
                >
                  Termenii și Condițiile
                </button>
                .*
              </span>
            </CheckboxLabel>
            {errors.acord1 && <CheckboxError>{errors.acord1}</CheckboxError>}

            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="acord2"
                checked={form.acord2}
                onChange={handleChange}
              />
              <span>
                Sunt de acord ca datele mele să fie prelucrate conform{" "}
                <button
                  type="button"
                  onClick={() => setShowTermsModal("privacy")}
                  style={{ color: "#ff77b4" }}
                >
                  politicii de confidențialitate
                </button>
                .*
              </span>
            </CheckboxLabel>
            {errors.acord2 && <CheckboxError>{errors.acord2}</CheckboxError>}

            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="acord3"
                checked={form.acord3}
                onChange={handleChange}
              />
              <span>
                Sunt de acord să primesc oferte și promoții prin e-mail sau SMS.
                <strong
                  style={{
                    display: "block",
                    marginTop: "6px",
                    color: "#d95e97",
                    fontWeight: "800",
                    lineHeight: "1.4",
                  }}
                >
                  🎁 Bifează și primești pe e-mail un cupon unic de 10% reducere
                  pentru următoarea comandă, dacă nu ai mai primit deja unul.
                </strong>
              </span>
            </CheckboxLabel>
          </CheckboxContainer>

          <SubmitButton type="submit">Creează cont</SubmitButton>
          <Note>Toate câmpurile marcate cu * trebuie completate.</Note>
        </Form>

        {toastMsg && (
          <ToastModal
            message={toastMsg}
            onClose={() => {
              setToastMsg("");
              if (shouldNavigate)
                navigate("/login", { state: { scrollTo: "top" } });
            }}
          />
        )}

        {showTermsModal && (
          <TermsAndConditionsModal onClose={() => setShowTermsModal(false)} />
        )}
      </Container>
    </>
  );
}
